/**
 * The values a search filter can take.
 *
 * Deliberately independent of the `asset` enums, even where the literals
 * coincide: this is the vocabulary of a *query*, not of an asset. It carries
 * composites no asset is ever a member of (`creative,editorial`, `image,film`)
 * and omits asset values a visitor cannot search by (`MediaTypeEnum.UNKNOWN`).
 *
 * Where the literals overlap they must agree exactly — a search for
 * `assettype=image` only matches assets whose type is `image`. That agreement
 * is pinned by filterValues.test.ts rather than by importing the asset enums,
 * so the two concepts can diverge without one silently dragging the other.
 */

export enum AllowedUseSearchFilterEnum {
  ALL = '',
  EDITORIAL_USE_ONLY = 'editorialuseonly',
  AVAILABLE_FOR_ALL_USES = 'availableforalluses',
  OFF = 'off',
}

export enum PhraseProcessingSearchFilterEnum {
  EXCLUDE_NATURAL_LANGUAGE = 'excludenaturallanguage',
  DEFAULT = 'default',
}

export enum OfflineContentSearchFilterEnum {
  EXCLUDE = 'exclude',
  EXCLUSIVE = 'exclusive',
  INCLUDE = 'include',
}

export enum AssetFamilySearchFilterEnum {
  CREATIVE = 'creative',
  EDITORIAL = 'editorial',
  CREATIVE_AND_EDITORIAL = 'creative,editorial',
}

export enum AssetTypeSearchFilterEnum {
  IMAGE = 'image',
  FILM = 'film',
  IMAGE_AND_FILM = 'image,film',
  VIDEO_TEMPLATE = 'videotemplate',
}

export type Agreements = {
  premiumaccess?: string;
  easyaccess?: string;
  editorialsubscription?: string;
  royaltyfreesubscription?: string;
};

export enum MediaTypeSearchFilterEnum {
  PHOTOGRAPHY = 'photography',
  ILLUSTRATION = 'illustration',
}

export enum FileTypeSearchFilterEnum {
  EPS = 'eps',
}

/**
 * What the license filter is set to — one license or a combination of two.
 *
 * The combinations are values in their own right, not a list that happens to
 * be written with a comma: the url, the search service and the filter panel's
 * Select each exchange one of these as a single token. `licensesOf` reads the
 * individual licenses back out when a rule needs them.
 */
export enum LicenseSearchFilterEnum {
  ROYALITY_FREE = 'rf',
  RIGHTS_READY = 'rr',
  RIGHTS_MANAGED = 'rm',
  ROYALITY_FREE_AND_RIGHTS_MANAGED = 'rf,rm',
  ROYALITY_FREE_AND_RIGHTS_READY = 'rf,rr',
}

/** The individual licenses, in the order a combination names them. */
const LICENSES = [
  LicenseSearchFilterEnum.ROYALITY_FREE,
  LicenseSearchFilterEnum.RIGHTS_MANAGED,
  LicenseSearchFilterEnum.RIGHTS_READY,
] as const;

/** The licenses a token names, in that same order. */
export const licensesOf = (
  token: string | undefined
): LicenseSearchFilterEnum[] => {
  if (!token) return [];
  const named = new Set(token.toLowerCase().split(','));

  return LICENSES.filter((license) => named.has(license));
};

export enum SortEnum {
  BEST = 'best',
  NEWEST = 'newest',
  OLDEST = 'oldest',
  MOST_POPULAR = 'mostpopular',
}

// Label and service value deliberately differ. Once an endpoint exists in the
// BFF to support the GalleryHeader, this mapping could move there.
export enum ColorMoodEnum {
  ALL = '',
  WARM = 'warm',
  COOL = 'cool',
  VIVID = 'vivid',
  NATURAL = 'neutral',
  BOLD = 'dramatic',
  DRAMATIC = 'moody',
  BANDW = 'bandw',
}

export enum OrientationEnum {
  VERTICAL = 'vertical',
  HORIZONTAL = 'horizontal',
  SQUARE = 'square',
  PANORAMIC_HORIZONTAL = 'panoramichorizontal',
  PANORAMIC_VERTICAL = 'panoramicvertical',
}

export enum NumberOfPeopleEnum {
  NONE = 'none',
  ONE = 'one',
  TWO = 'two',
  GROUP = 'group',
}

export enum AgeOfPeopleEnum {
  BABY = 'baby',
  CHILD = 'child',
  TEENAGER = 'teenager',
  YOUNG_ADULT = 'youngadult',
  ADULT = 'adult',
  ADULTS_ONLY = 'adultsonly',
  MATURE_ADULT = 'matureadult',
  SENIOR_ADULT = 'senioradult',
}

export enum EditorialProductEnum {
  ALL = 'all',
  NEWS = 'news',
  SPORT = 'sport',
  ENTERTAINMENT = 'entertainment',
  ARCHIVAL = 'archival',
}

export enum EthnicityEnum {
  BLACK = 'black',
  CAUCASIAN = 'caucasian',
  EAST_ASIAN = 'eastasian',
  HISPANIC_LATINO = 'hispaniclatino',
  MIDDLE_EASTERN = 'middleeastern',
  MIXED_RACE_PERSON = 'mixedraceperson',
  MULTI_ETHNIC_GROUP = 'multiethnicgroup',
  NATIVE_AMERICAN_FIRST_NATIONS = 'nativeamericanfirstnations',
  PACIFIC_ISLANDER = 'pacificislander',
  SOUTH_ASIAN = 'southasian',
  SOUTHEAST_ASIAN = 'southeastasian',
}

export enum CompositionSearchFilterEnum {
  // People composition
  HEADSHOT = 'headshot',
  WAIST_UP = 'waistup',
  THREE_QUARTER_LENGTH = 'threequarterlength',
  FULL_LENGTH = 'fulllength',
  LOOKING_AT_CAMERA = 'lookingatcamera',
  CANDID = 'candid',
  MEDIUM_SHOT = 'mediumshot',
  WIDE_SHOT = 'wideshot',
  CLOSE_UP = 'closeup',
  PART_OF_A_SERIES = 'partofaseries',
  ABSTRACT = 'abstract',
  PORTRAIT = 'portrait',
  SPARSE = 'sparse',
  CUTOUT = 'cutout',
  FULL_FRAME = 'fullframe',
  COPY_SPACE = 'copyspace',
  MACRO = 'macro',
  STILL_LIFE = 'stilllife',
}

export enum FilmDefinitionSearchFilterEnum {
  FOUR_K = '4k',
  HD = 'hd',
  SD = 'sd',
}

export enum FrameRateEnum {
  TWENTY_THREE_NINETY_EIGHT = '23.98',
  TWENTY_FOUR = '24',
  TWENTY_FIVE = '25',
  TWENTY_NINE_NINETY_SEVEN = '29.97',
  THIRTY = '30',
  FIFTY = '50',
  FIFTY_NINE_NINETY_FOUR = '59.94',
  SIXTY = '60',
}

export enum ViewPointEnum {
  LOCKDOWN = 'lockdown',
  PANNING = 'panning',
  TRACKING_SHOT = 'trackingshot',
  AERIAL_VIEW = 'aerialview',
  HIGH_ANGLE_VIEW = 'highangleview',
  LOW_ANGLE_VIEW = 'lowangleview',
  TILT = 'tilt',
  POINT_OF_VIEW = 'pointofview',
}

export enum ImageTechniqueEnum {
  REALTIME = 'realtime',
  TIMELAPSE = 'timelapse',
  SLOWMOTION = 'slowmotion',
  COLOR = 'color',
  BLACK_AND_WHITE = 'blackandwhite',
  ANIMATION = 'animation',
  SELECTIVE_FOCUS = 'selectivefocus',
}

export enum VideoTemplateCategoryEnum {
  BACKGROUNDS = 'backgrounds',
  ELEMENTS = 'elements',
  INFOGRAPHICS = 'infographics',
  LOGO_ANIMATIONS = 'logoanimations',
  LOWER_THIRDS = 'lowerthirds',
  OPENERS = 'openers',
  PRESENTATIONS = 'presentations',
  PROMOS = 'promos',
  TITLES = 'titles',
  TRANSITIONS = 'transitions',
}

export enum VideoTemplateAppVersionEnum {
  AFTER_EFFECTS_CC = 'aftereffectscc',
  AFTER_EFFECTS_CS6 = 'aftereffectscs6',
  AFTER_EFFECTS_CS5 = 'aftereffectscs5',
  AFTER_EFFECTS_CS5_5 = 'aftereffectscs5_5',
  AFTER_EFFECTS_CS4 = 'aftereffectscs4',
  AFTER_EFFECTS_CS3 = 'aftereffectscs3',
}

export enum IstockCollectionSearchFilterEnum {
  ALL = '',
  ESSENTIALS = 'main,value',
  SIGNATURE = 'signature,signatureplus',
}

// The filter has exactly one valid value: it is on when the parameter is
// present and off when it is absent. The URL's off form (`istockexclusive=no`,
// produced by the facet redirect rules) is not a member, so it parses to unset.
export enum IstockExclusiveSearchFilterEnum {
  YES = 'yes',
}

export enum RecencyEnum {
  ANY_DATE = 'anydate',
  NOW = 'now',
  DATE_RANGE = 'daterange',
  LAST_24_HOURS = 'last24hours',
  LAST_48_HOURS = 'last48hours',
  LAST_72_HOURS = 'last72hours',
  LAST_7_DAYS = 'last7days',
  LAST_30_DAYS = 'last30days',
  LAST_12_MONTHS = 'last12months',
}

export enum ColorSearchFilterEnum {
  BLACK_AND_WHITE = 'bw',
}

export enum ImageSizeSearchFilterEnum {
  ALL = '',
  LARGE = 'large',
  XLARGE = 'xlarge',
  XXLARGE = 'xxlarge',
  XXXLARGE = 'xxxlarge',
}

export enum ReleaseStatusSearchFilterEnum {
  NOT_IMPORTANT = '',
  FULLY_RELEASED = 'fullyreleased',
}
